import React, { useState, useEffect } from 'react';
import { Camera, Heart, MapPin, Users, X, ChevronLeft, ChevronRight, Filter, Grid, Calendar } from 'lucide-react';
import ImageWithFallback from './ImageWithFallback';

const ImageGallery = () => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [lightboxImage, setLightboxImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiCategories, setApiCategories] = useState({});

  // Filtres disponibles
  const filters = [
    { id: 'all', name: 'Toutes les photos', icon: Grid, count: 0 },
    { id: 'community', name: 'Vie communautaire', icon: Users, count: 0 },
    { id: 'marriage', name: 'Mariages & Célébrations', icon: Heart, count: 0 },
    { id: 'pilgrimage', name: 'Pèlerinages & Retraites', icon: MapPin, count: 0 },
    { id: 'culture', name: 'Culture béninoise', icon: Calendar, count: 0 },
    { id: 'architecture', name: 'Lieux saints', icon: Camera, count: 0 }
  ];

  // Charger les images depuis l'API
  useEffect(() => {
    const fetchGalleryImages = async () => {
      try {
        setLoading(true);
        const response = await fetch('http://localhost:8000/api/public/gallery-images');
        
        if (response.ok) {
          const data = await response.json();
          console.log('Données API reçues:', data);
          console.log('Images récupérées:', data.data);
          setGalleryImages(data.data || []);
          setApiCategories(data.filters?.categories || {});
        } else {
          console.error('Erreur lors du chargement des images');
          // Utiliser les données statiques en cas d'erreur
          setGalleryImages(staticGalleryImages);
        }
      } catch (error) {
        console.error('Erreur de connexion:', error);
        // Utiliser les données statiques en cas d'erreur
        setGalleryImages(staticGalleryImages);
      } finally {
        setLoading(false);
      }
    };

    fetchGalleryImages();
  }, []);

  // Images statiques de fallback
  const staticGalleryImages = [
    // Photos de la communauté
    {
      id: 1,
      title: "Assemblée générale FSF 2024",
      location: "Calavi, Bénin",
      description: "Rassemblement annuel de tous les couples de la communauté FSF",
      category: "community",
      date: "Mars 2024",
      photographer: "Équipe FSF",
      image: "/assets/images/couple_enfant.png",
      fallbackType: "family"
    },
    {
      id: 2,
      title: "Couples en prière matinale",
      location: "Porto-Novo, Bénin", 
      description: "Moment de prière commune avant la messe dominicale",
      category: "community",
      date: "Février 2024",
      photographer: "Marie Agbodjan",
      image: "/assets/images/couple_enfant.png",
      fallbackType: "prayer"
    },
    {
      id: 3,
      title: "Formation des jeunes couples",
      location: "Cotonou, Bénin",
      description: "Session de formation sur la spiritualité conjugale",
      category: "community",
      date: "Janvier 2024", 
      photographer: "Pierre Hounou",
     image: "/assets/images/couple_enfant.png",
      fallbackType: "couple"
    },

    // Mariages et célébrations
    {
      id: 4,
      title: "Mariage de Sarah & Emmanuel",
      location: "Cathédrale de Cotonou",
      description: "Célébration du sacrement de mariage selon les traditions FSF",
      category: "marriage",
      date: "Avril 2024",
      photographer: "Studio Bénin Photo",
      image: "/assets/images/couple_enfant.png",
      fallbackType: "couple"
    },
    {
      id: 5,
      title: "Bénédiction nuptiale traditionnelle",
      location: "Ouidah, Bénin",
      description: "Cérémonie alliant traditions béninoises et rites catholiques",
      category: "marriage", 
      date: "Mai 2024",
      photographer: "Culture Photo",
      image: "/assets/images/couple_enfant.png",
      fallbackType: "family"
    },
    {
      id: 6,
      title: "Première communion familiale",
      location: "Parakou, Bénin",
      description: "Enfants FSF recevant leur première communion",
      category: "marriage",
      date: "Juin 2024", 
      photographer: "Famille Kpogbe",
      image: "/assets/images/couple.png",
      fallbackType: "family"
    },

    // Pèlerinages et retraites
    {
      id: 7,
      title: "Pèlerinage à Notre-Dame d'Arigbo",
      location: "Dassa-Zoumé, Bénin",
      description: "Pèlerinage annuel des familles FSF au sanctuaire marial",
      category: "pilgrimage",
      date: "Août 2024",
      photographer: "Équipe Pèlerinage",
      image: "/assets/images/couple.png",
      fallbackType: "church"
    },
    {
      id: 8,
      title: "Retraite spirituelle des couples",
      location: "Abomey-Calavi, Bénin", 
      description: "Week-end de ressourcement spirituel pour les couples",
      category: "pilgrimage",
      date: "Septembre 2024",
      photographer: "Centre Saint-Joseph",
      image: "/assets/images/couple.png",
      fallbackType: "couple"
    },
    {
      id: 9,
      title: "Chemin de croix communautaire", 
      location: "Ganvié, Bénin",
      description: "Chemin de croix pendant la Semaine Sainte sur l'eau",
      category: "pilgrimage",
      date: "Mars 2024",
      photographer: "Jean-Baptiste Adjovi",
      image: "/assets/images/couple.png",
      fallbackType: "church"
    },

    // Culture béninoise
    {
      id: 10,
      title: "Chants liturgiques en fongbé",
      location: "Abomey, Bénin",
      description: "Chorale FSF interpretant les cantiques en langues locales", 
      category: "culture",
      date: "Décembre 2023",
      photographer: "Traditions FSF",
      image: "/assets/images/couple.png",
      fallbackType: "family"
    },
    {
      id: 11,
      title: "Danses traditionnelles à l'église",
      location: "Natitingou, Bénin",
      description: "Inculturation de la liturgie avec les danses béninoises",
      category: "culture", 
      date: "Novembre 2023",
      photographer: "Culture Bénin",
      image: "/assets/images/couple.png",
      fallbackType: "church"
    },
    {
      id: 12,
      title: "Repas communautaire traditionnel",
      location: "Bohicon, Bénin",
      description: "Partage du repas après la célébration dominicale",
      category: "culture",
      date: "Octobre 2024",
      photographer: "Famille Dossou",
      image: "/assets/images/couple.png",
      fallbackType: "family"
    },

    // Lieux saints et architecture
    {
      id: 13,
      title: "Cathédrale Notre-Dame de Miséricorde",
      location: "Cotonou, Bénin",
      description: "Cathédrale principale où se rassemblent les couples FSF",
      category: "architecture",
      date: "Toute l'année",
      photographer: "Archives FSF",
      image: "/assets/images/couple.png",
      fallbackType: "church"
    },
    {
      id: 14,
      title: "Basilique de l'Immaculée Conception",
      location: "Ouidah, Bénin", 
      description: "Lieu de pèlerinage important pour la communauté FSF",
      category: "architecture",
      date: "Patrimoine",
      photographer: "Patrimoine Bénin",
      image: "/assets/images/couple.png",
      fallbackType: "church"
    },
    {
      id: 15,
      title: "Chapelle du Centre FSF",
      location: "Calavi, Bénin",
      description: "Chapelle privée de la communauté pour les formations",
      category: "architecture",
      date: "Janvier 2024",
      photographer: "Architecture FSF", 
      image: "/assets/images/couple.png",
      fallbackType: "church"
    }
  ];

  // Calculer le nombre d'images par catégorie
  const updateFilterCounts = () => {
    return filters.map(filter => ({
      ...filter,
      count: filter.id === 'all' ? galleryImages.length : galleryImages.filter(img => img.category === filter.id).length
    }));
  };

  const filtersWithCounts = updateFilterCounts();

  // Filtrer les images
  const filteredImages = selectedFilter === 'all' 
    ? galleryImages 
    : galleryImages.filter(image => image.category === selectedFilter);

  // Fonctions pour la lightbox
  const openLightbox = (image) => {
    setLightboxImage(image);
    setCurrentImageIndex(filteredImages.findIndex(img => img.id === image.id));
  };

  const closeLightbox = () => {
    setLightboxImage(null);
  };

  const nextImage = () => {
    const nextIndex = (currentImageIndex + 1) % filteredImages.length;
    setCurrentImageIndex(nextIndex);
    setLightboxImage(filteredImages[nextIndex]);
  };

  const prevImage = () => {
    const prevIndex = (currentImageIndex - 1 + filteredImages.length) % filteredImages.length;
    setCurrentImageIndex(prevIndex);
    setLightboxImage(filteredImages[prevIndex]);
  };

  const getCategoryColor = (category) => {
    const colors = {
      community: 'from-blue-500 to-blue-700',
      marriage: 'from-pink-500 to-red-600',
      pilgrimage: 'from-green-500 to-emerald-700',
      culture: 'from-purple-500 to-indigo-700', 
      architecture: 'from-yellow-500 to-orange-600'
    };
    return colors[category] || 'from-gray-500 to-gray-700';
  };

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'architecture': return '⛪';
      case 'community': return '👥';
      case 'marriage': return '💒';
      case 'pilgrimage': return '🙏';
      case 'culture': return '🎵';
      case 'charity': return '❤️';
      default: return '📸';
    }
  };

  return (
    <>
      <section id="galerie" className="py-20 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="container mx-auto px-4">
          {/* En-tête */}
          <div className="text-center mb-16">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <Camera className="w-8 h-8 text-blue-600" />
              <h2 className="text-4xl md:text-5xl font-bold text-gray-800 font-serif">
                Galerie Photo FSF
              </h2>
              <Camera className="w-8 h-8 text-blue-600" />
            </div>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto text-justify">
              Découvrez la vie de notre communauté à travers des moments de foi, de joie et de partage 
              dans les traditions béninoises et catholiques.
            </p>
          </div>

          {/* Filtres */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {filtersWithCounts.map((filter) => {
              const IconComponent = filter.icon;
              return (
                <button
                  key={filter.id}
                  onClick={() => setSelectedFilter(filter.id)}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 ${
                    selectedFilter === filter.id
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                  }`}
                >
                  <IconComponent className="w-5 h-5" />
                  <span>{filter.name}</span>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    selectedFilter === filter.id ? 'bg-white/20' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {filter.count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Grille des images */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {loading ? (
              // Indicateur de chargement
              Array.from({ length: 8 }).map((_, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden animate-pulse">
                  <div className="h-64 bg-gray-300"></div>
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                    <div className="h-3 bg-gray-300 rounded w-full"></div>
                    <div className="h-3 bg-gray-300 rounded w-2/3"></div>
                  </div>
                </div>
              ))
            ) : (
              filteredImages.map((image) => (
              <div 
                key={image.id} 
                onClick={() => openLightbox(image)}
                className="group relative bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer"
              >
                {/* Image */}
                <div className="relative h-64 overflow-hidden">
                  <ImageWithFallback
                    src={image.image_url}
                    fallbackType={image.fallback_type}
                    alt={image.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    index={image.id}
                  />
                  
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-80"></div>
                  
                  {/* Badge catégorie */}
                  <div className="absolute top-4 right-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold text-white bg-gradient-to-r ${getCategoryColor(image.category)}`}>
                      {image.category_label || filters.find(f => f.id === image.category)?.name}
                    </span>
                  </div>

                  {/* Icône d'agrandissement */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                      <Camera className="w-8 h-8 text-white" />
                    </div>
                  </div>
                </div>

                {/* Informations */}
                <div className="p-4">
                  <h3 className="font-bold text-gray-800 mb-2 line-clamp-2 text-lg">
                    {image.title}
                  </h3>
                  
                  <div className="flex items-center text-gray-600 text-sm mb-2">
                    <MapPin className="w-4 h-4 mr-1" />
                    <span>{image.location}</span>
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2 text-justify">
                    {image.description}
                  </p>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className="flex items-center">
                      <Calendar className="w-3 h-3 mr-1" />
                      {image.date}
                    </span>
                    <span>📸 {image.photographer}</span>
                  </div>
                </div>
              </div>
            ))
            )}
          </div>

          {/* Message si aucune image */}
          {filteredImages.length === 0 && (
            <div className="text-center py-16">
              <Camera className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">Aucune image trouvée</h3>
              <p className="text-gray-500">Essayez de changer de filtre pour voir plus de photos.</p>
            </div>
          )}

          {/* Statistiques */}
          {/* <div className="mt-16 bg-white rounded-2xl shadow-lg p-8 max-w-4xl mx-auto">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Notre galerie en chiffres</h3>
              <p className="text-gray-600 text-justify">Découvrez l'activité photographique de notre communauté</p>
            </div>
            <div className="grid md:grid-cols-4 gap-6 text-center">
              <div className="space-y-2">
                <div className="text-3xl font-bold text-blue-600">{galleryImages.length}</div>
                <div className="text-gray-600">Photos totales</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl font-bold text-purple-600">6</div>
                <div className="text-gray-600">Catégories</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl font-bold text-green-600">2024</div>
                <div className="text-gray-600">Année active</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl font-bold text-red-600">10+</div>
                <div className="text-gray-600">Photographes</div>
              </div>
            </div>
          </div> */}
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <div 
            className="relative max-w-4xl max-h-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Image principale */}
            <div className="relative">
              <ImageWithFallback
                src={lightboxImage.image_url}
                fallbackType={lightboxImage.fallback_type}
                alt={lightboxImage.title}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
                index={lightboxImage.id}
              />
              
              {/* Bouton fermer */}
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors duration-200"
              >
                <X className="w-6 h-6" />
              </button>

              {/* Navigation */}
              {filteredImages.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors duration-200"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors duration-200"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}
            </div>

            {/* Informations de l'image */}
            <div className="bg-white rounded-lg p-6 mt-4">
              <h3 className="text-xl font-bold text-gray-800 mb-2">{lightboxImage.title}</h3>
              <div className="flex items-center text-gray-600 mb-2">
                <MapPin className="w-4 h-4 mr-2" />
                <span>{lightboxImage.location}</span>
                <span className="mx-2">•</span>
                <Calendar className="w-4 h-4 mr-2" />
                <span>{lightboxImage.date}</span>
              </div>
              <p className="text-gray-700 mb-3 text-justify">{lightboxImage.description}</p>
              <div className="flex items-center justify-between text-sm text-gray-500">
                <span>📸 Photo par: {lightboxImage.photographer}</span>
                <span className={`px-2 py-1 rounded-full text-white text-xs bg-gradient-to-r ${getCategoryColor(lightboxImage.category)}`}>
                  {filters.find(f => f.id === lightboxImage.category)?.name}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageGallery;